// ***************************************************************************************
// HUNT MODE's achievements.
//
// Ten kinds exist; FIVE are active on any given day, chosen from the daily seed. Each is a
// standing challenge - play a word that satisfies it and it is struck through, and that
// word scores double.
//
// WHY THEY ARE PUBLISHED DATA RATHER THAN CODE THAT RUNS TWICE.
//
// Achievements change the SCORE, so the Worker has to agree with the browser about which
// ones were active and which were claimed. The generator therefore picks the day's five
// at publication time and writes them into the puzzle row; both sides then read the same
// five out of the same puzzle. Deriving them independently on each side would be two
// implementations of one decision, and those drift.
//
// The engine still evaluates them - `claimedBy` below is what decides a score, and it runs
// unchanged in the browser and in the Worker.
//
// THE DEFINITION KIND is the reason `label` is stored rather than composed at display
// time: its text comes from WordNet via definitions.bin, a 684KB file that only the
// generator has any reason to read.
// ***************************************************************************************

import type { Move } from './modes.ts';
import { TIER_COOL } from './rules.ts';

export const ACHIEVEMENTS_PER_DAY = 5;

export type AchievementKind =
  | 'starts-with'
  | 'ends-with'
  | 'exact-length'
  | 'definition'
  | 'rare-letter'
  | 'double-letter'
  | 'cool-word'
  | 'big-score'
  | 'many-vowels'
  | 'diagonal-step';

export interface Achievement {
  readonly id: string;
  readonly kind: AchievementKind;
  /** What the player is told. Composed once, at publication. */
  readonly label: string;

  readonly letter?: string;
  readonly length?: number;
  /** The answer, for `definition`. Never shown - the label carries the clue. */
  readonly word?: string;
  readonly minScore?: number;
  readonly minVowels?: number;

  /**
   * ONE WORD ON THIS BOARD THAT TAKES IT.
   *
   * The generator finds it while checking the challenge is achievable at all, so this is
   * not a hint invented afterwards - it is the proof, kept. Boards offer hundreds of
   * words and the published route uses seven of them, so "is the answer even here?" is
   * otherwise unanswerable from the outside; this is the answer to that question.
   *
   * SPOILER. The Worker strips it unless the deployment reveals solutions, and the
   * client only shows it inside the solution panel, which is closed until asked for.
   */
  readonly example?: string;
}

/** All ten kinds, for documentation and for the generator to pick from. */
export const ACHIEVEMENT_KINDS: readonly AchievementKind[] = [
  'starts-with',
  'ends-with',
  'exact-length',
  'definition',
  'rare-letter',
  'double-letter',
  'cool-word',
  'big-score',
  'many-vowels',
  'diagonal-step',
];

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);
const RARE_LETTERS = new Set(['j', 'q', 'x', 'z']);

/**
 * Does this move satisfy this achievement?
 *
 * Pure, and the single authority. The UI uses it to show a claim, the engine uses it to
 * score one, and the Worker uses it to check one - all the same function.
 */
export function satisfies(achievement: Achievement, move: Move): boolean {
  const word = move.word;

  switch (achievement.kind) {
    case 'starts-with':
      return achievement.letter !== undefined && word.startsWith(achievement.letter);

    case 'ends-with':
      return achievement.letter !== undefined && word.endsWith(achievement.letter);

    case 'exact-length':
      return achievement.length !== undefined && word.length === achievement.length;

    case 'definition':
      return achievement.word !== undefined && word === achievement.word;

    case 'rare-letter':
      return [...word].some((c) => RARE_LETTERS.has(c));

    case 'double-letter':
      // Two of the SAME letter side by side, which is a property of the word rather than
      // of the board - 'll' in 'ball', not two tiles that happen to match.
      return [...word].some((c, i) => i > 0 && c === word[i - 1]);

    case 'cool-word':
      return move.tier === TIER_COOL;

    case 'big-score':
      // Against the BASE score of the word. Comparing against the doubled score would
      // make the achievement partly depend on itself.
      return achievement.minScore !== undefined && move.score >= achievement.minScore;

    case 'many-vowels':
      return (
        achievement.minVowels !== undefined &&
        [...word].filter((c) => VOWELS.has(c)).length >= achievement.minVowels
      );

    case 'diagonal-step':
      return move.tiles.some((tile, i) => {
        if (i === 0) return false;

        const previous = move.tiles[i - 1]!;

        return tile.x !== previous.x && tile.y !== previous.y;
      });

    default:
      return false;
  }
}

/**
 * Which of the still-unclaimed achievements this move takes.
 *
 * A word may take more than one. The alternative - first match only - means a word that
 * happens to satisfy two feels like it was short-changed, and there is no reading of
 * "award double the value" that makes taking the second one worth nothing.
 */
export function claimedBy(
  achievements: readonly Achievement[],
  alreadyClaimed: ReadonlySet<string>,
  move: Move,
): string[] {
  const taken: string[] = [];

  for (const achievement of achievements) {
    if (alreadyClaimed.has(achievement.id)) continue;
    if (!satisfies(achievement, move)) continue;

    taken.push(achievement.id);
  }

  return taken;
}

/**
 * The multiplier a word earns for what it claimed.
 *
 * One achievement doubles it, which is the rule as stated. Two triples it, three
 * quadruples it - each claim is worth another copy of the word, rather than doubling the
 * double, because 4x for two is a much bigger prize than "double" suggests.
 */
export function achievementMultiplier(claimedCount: number): number {
  return 1 + claimedCount;
}

/** Parse achievements off a stored puzzle. Unknown kinds are dropped, not guessed at. */
export function readAchievements(raw: unknown): Achievement[] {
  if (!Array.isArray(raw)) return [];

  const known = new Set<string>(ACHIEVEMENT_KINDS);
  const out: Achievement[] = [];

  for (const entry of raw) {
    const item = entry as Partial<Achievement>;

    if (typeof item?.id !== 'string' || typeof item?.kind !== 'string') continue;
    if (!known.has(item.kind)) continue;
    if (typeof item.label !== 'string') continue;

    out.push({
      id: item.id,
      kind: item.kind as AchievementKind,
      label: item.label,
      ...(typeof item.letter === 'string' ? { letter: item.letter } : {}),
      ...(typeof item.length === 'number' ? { length: item.length } : {}),
      ...(typeof item.word === 'string' ? { word: item.word } : {}),
      ...(typeof item.minScore === 'number' ? { minScore: item.minScore } : {}),
      ...(typeof item.minVowels === 'number' ? { minVowels: item.minVowels } : {}),
      ...(typeof item.example === 'string' ? { example: item.example } : {}),
    });
  }

  return out;
}
